import { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { Modal, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';

//components
import withLocalization from '~/hoc/withLocalization';

//elements
import Button from '../../../components/CustomButton/CustomButton.jsx';
import StatusBadge from '../../../elements/StatusBadge.js';
import LoadingSpinner from '../../../elements/LoadingSpinner.js';

//utils
import { shortDateFormat, dateToTimeFormat, totalTimeFormat } from '~/utils/dateFormat';
import {
    overtime_transaction_type_map,
    overtime_transaction_type_color_map,
    overtime_transaction_overtime_type_map,
    overtime_transaction_overtime_type_color_map,
    overtime_transaction_status_map,
    overtime_transaction_status_color_map,
} from '../../../utils/normalStatuses.js';

//assets
import CloseModalIcon from '~/assets/img/CloseApproveModalIcon.svg';

//styles
import '../../../styles/css/timelogs.css';

const STATUS_PILL_WIDTH = 130;

const DetailRow = ({ label, children }) => (
    <tr>
        <td className="fw-bold bg-light text-start" style={{ width: '35%' }}>{label}</td>
        <td className="bg-white text-start">{children}</td>
    </tr>
);

const ViewOvertimeTransactionModal = ({ transactionId, groupParams, onClose, overtimeBankStore, commonStore, t }) => {
    const [activeTransactionId, setActiveTransactionId] = useState(transactionId || null);
    const [transaction, setTransaction] = useState(null);
    const [loadingTransaction, setLoadingTransaction] = useState(!!activeTransactionId);
    const [groupTransactions, setGroupTransactions] = useState([]);
    const [groupSummary, setGroupSummary] = useState(null);
    const [loadingGroup, setLoadingGroup] = useState(!!groupParams);
    const dateTimeRules = commonStore.getDateTimeRules();

    useEffect(() => {
        if (!groupParams) return undefined;
        let cancelled = false;
        setLoadingGroup(true);
        overtimeBankStore.loadOvertimeTransactionsForDateAndType({
            user_id: groupParams.userId,
            for_date: groupParams.forDate,
            overtime_type: groupParams.overtimeType,
        })
            .then(result => {
                if (!cancelled) {
                    setGroupTransactions(result?.overtimeTransactions || []);
                    setGroupSummary(result?.summary || null);
                }
            })
            .catch(error => {
                console.error(error);
                commonStore.addNotification(error.message || t('Something went wrong'), null, 'error');
                if (!cancelled) onClose();
            })
            .finally(() => {
                if (!cancelled) setLoadingGroup(false);
            });
        return () => { cancelled = true; };
    }, [groupParams?.userId, groupParams?.forDate, groupParams?.overtimeType]);

    useEffect(() => {
        if (!activeTransactionId) return undefined;
        let cancelled = false;
        setLoadingTransaction(true);
        overtimeBankStore.loadOvertimeTransaction(activeTransactionId)
            .then(result => {
                if (!cancelled) setTransaction(result);
            })
            .catch(error => {
                console.error(error);
                commonStore.addNotification(error.message || t('Something went wrong'), null, 'error');
                if (!cancelled) {
                    if (groupParams) setActiveTransactionId(null);
                    else onClose();
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingTransaction(false);
            });
        return () => { cancelled = true; };
    }, [activeTransactionId]);

    const showingSingle = !!activeTransactionId;
    const canGoBack = showingSingle && !!groupParams;
    const goBack = () => {
        setTransaction(null);
        setActiveTransactionId(null);
    };

    return (
        <Modal dialogClassName="lg" className="lg" size="lg" show backdrop="static" onHide={onClose}>
            <Modal.Header className="set_ModalHeader">
                <Modal.Title>
                    {t('Overtime Transaction')}
                    {!showingSingle && groupParams ? ` - ${shortDateFormat(groupParams.forDate, dateTimeRules)}` : ''}
                </Modal.Title>
                <img src={CloseModalIcon} className="cursor-pointer" onClick={onClose} alt={'close-icon'} />
            </Modal.Header>
            <Modal.Body style={{ minHeight: 300 }}>
                {showingSingle ?
                    (loadingTransaction || !transaction ?
                        <LoadingSpinner />
                        :
                        <table className="table table-bordered mb-0">
                            <tbody>
                                <DetailRow label={t('Date')}>
                                    {shortDateFormat(transaction.for_date, dateTimeRules)}
                                </DetailRow>
                                <DetailRow label={t('Hours')}>
                                    {totalTimeFormat(transaction.overtime_hours_in_mins, dateTimeRules)}
                                </DetailRow>
                                <DetailRow label={t('Overtime Type')}>
                                    <StatusBadge
                                        label={t(overtime_transaction_overtime_type_map[transaction.overtime_type] || transaction.overtime_type)}
                                        color={overtime_transaction_overtime_type_color_map[transaction.overtime_type]}
                                        outline
                                        style={{ width: STATUS_PILL_WIDTH }}
                                    />
                                </DetailRow>
                                <DetailRow label={t('Transaction Type')}>
                                    <StatusBadge
                                        label={t(overtime_transaction_type_map[transaction.transaction_type] || transaction.transaction_type)}
                                        color={overtime_transaction_type_color_map[transaction.transaction_type]}
                                        outline
                                        style={{ width: STATUS_PILL_WIDTH }}
                                    />
                                </DetailRow>
                                <DetailRow label={t('Reason')}>
                                    {(transaction.reason_text ? t(transaction.reason_text) : transaction.reason) || '-'}
                                </DetailRow>
                                <DetailRow label={t('Created At')}>
                                    {shortDateFormat(transaction.created_at, dateTimeRules)} {dateToTimeFormat(transaction.created_at, dateTimeRules)}
                                </DetailRow>
                                <DetailRow label={t('Status')}>
                                    <StatusBadge
                                        label={t(overtime_transaction_status_map[transaction.status] || transaction.status)}
                                        color={overtime_transaction_status_color_map[transaction.status]}
                                        style={{ width: STATUS_PILL_WIDTH }}
                                    />
                                    {transaction.created_by_name && <span className="fs12 ms-2">{transaction.created_by_name}</span>}
                                </DetailRow>
                            </tbody>
                        </table>
                    )
                    :
                    (loadingGroup ?
                        <LoadingSpinner />
                        :
                        <>
                            {groupSummary &&
                                <div className='row timelog-grid-time-card mb-3'>
                                    <Col xs={12} md={4} className='timelog-grid-item justify-content-space-between card-timelog-grid-item mb-2'>
                                        <div className='d-flex align-items-center'>
                                            <i className='timelog-grid__icon' />
                                            <span className='timelog-grid__text w-auto'>{t('Date')}</span>
                                        </div>
                                        <b className='timelog-final-time-card-modal align-self-center'>
                                            {shortDateFormat(groupParams.forDate, dateTimeRules)}
                                        </b>
                                    </Col>
                                    <Col xs={12} md={4} className='timelog-grid-item justify-content-space-between card-timelog-grid-item mb-2'>
                                        <div className='d-flex align-items-center'>
                                            <i className='timelog-grid__icon' />
                                            <span className='timelog-grid__text w-auto'>{t('Type')}</span>
                                        </div>
                                        <b className='timelog-final-time-card-modal align-self-center'>
                                            <StatusBadge
                                                label={t(overtime_transaction_type_map[groupSummary.transaction_type] || groupSummary.transaction_type)}
                                                color={overtime_transaction_type_color_map[groupSummary.transaction_type]}
                                                outline
                                                style={{ width: STATUS_PILL_WIDTH }}
                                            />
                                        </b>
                                    </Col>
                                    <Col xs={12} md={4} className='timelog-grid-item justify-content-space-between card-timelog-grid-item mb-2'>
                                        <div className='d-flex align-items-center'>
                                            <i className='timelog-grid__icon' />
                                            <span className='timelog-grid__text w-auto'>{t('Hours')}</span>
                                        </div>
                                        <b className='timelog-final-time-card-modal align-self-center'>
                                            {totalTimeFormat(groupSummary.overtime_hours_in_mins, dateTimeRules)}
                                        </b>
                                    </Col>
                                </div>
                            }
                            <div className="projectMemberForm">
                            <table className="table table-bordered table-scroll mb-0">
                            <thead>
                                <tr>
                                    <th className="text-start">{t('Hours')}</th>
                                    <th className="text-center">{t('Transaction Type')}</th>
                                    <th className="text-start">{t('Reason')}</th>
                                    <th className="text-center">{t('Status')}</th>
                                    <th className="text-start">{t('Created At')}</th>
                                    <th className="text-center">{t('Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupTransactions.length ? groupTransactions.map(row => (
                                    <tr key={row.id}>
                                        <td className="text-start">{totalTimeFormat(row.overtime_hours_in_mins, dateTimeRules)}</td>
                                        <td className="d-flex justify-content-center">
                                            <StatusBadge
                                                label={t(overtime_transaction_type_map[row.transaction_type] || row.transaction_type)}
                                                color={overtime_transaction_type_color_map[row.transaction_type]}
                                                outline
                                                style={{ width: STATUS_PILL_WIDTH }}
                                            />
                                        </td>
                                        <td className="text-start">{(row.reason_text ? t(row.reason_text) : row.reason) || '-'}</td>
                                        <td className="d-flex justify-content-center">
                                            <StatusBadge
                                                label={
                                                    <div className='d-flex flex-column align-items-center'>
                                                        <span>{t(overtime_transaction_status_map[row.status] || row.status)}</span>
                                                        {row.created_by_name && <span className='fs10'>{row.created_by_name}</span>}
                                                    </div>
                                                }
                                                color={overtime_transaction_status_color_map[row.status]}
                                                style={{ width: STATUS_PILL_WIDTH }}
                                            />
                                        </td>
                                        <td className="text-start">
                                            {shortDateFormat(row.created_at, dateTimeRules)} {dateToTimeFormat(row.created_at, dateTimeRules)}
                                        </td>
                                        <td className="text-center">
                                            <OverlayTrigger placement="top" overlay={<Tooltip id={`view-transaction-${row.id}`}>{t('View Details')}</Tooltip>}>
                                                <Button icon_sm fill onClick={() => setActiveTransactionId(row.id)}>
                                                    <i className='fa fa-eye' />
                                                </Button>
                                            </OverlayTrigger>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={6} className="text-center">{t('No data found')}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        </div>
                        </>
                    )
                }
            </Modal.Body>
            <Modal.Footer>
                {canGoBack &&
                    <Button fill wd onClick={goBack}>
                        <i className="fa fa-arrow-left margin-right-10" /> {t('Back')}
                    </Button>
                }
                <Button wd onClick={onClose}>
                    {t('Close')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default inject('overtimeBankStore', 'commonStore')(withLocalization(observer(ViewOvertimeTransactionModal)));
